<template>
  <div class="w-100 p-2 mt-2">
    <page-header>
      <i class="fas fa-list"></i> {{ $t('page.title.instant_tournament_history') }}
      <instant-tournament-history-ladder-modal ref="instant-tournament-history-ladder-modal"></instant-tournament-history-ladder-modal>
    </page-header>
    <page-card>
      <template v-slot:header>
        {{ $t('page.filters')}}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <div class="row">
            <div class="col-4">
              <filter-input
                  :filter-query="filterQuery"
                  :default-filter="$route.query.id"
                  filter-operator="like"
                  filter-name="id"
                  :label="$t('filters.label.tournament_id')"
                  :placeholder="$t('filters.placeholder.tournament_id')">
              </filter-input>
            </div>

            <div class="col-4">
              <filter-input-with-operator
                  :filter-query="filterQuery"
                  :operator-options="filterOptions.comparisonOperator"
                  :operator-multiple="false"
                  :default-operator="{value: 'eq', text: $t('operator.equales')}"
                  :restrict-input="true"
                  restriction-type="decimals"
                  filter-name="buyIn"
                  :label="$t('filters.label.buy_in')"
                  :operator-placeholder="$t('filters.operator_placeholder.buy_in')"
              >
              </filter-input-with-operator>
            </div>


            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="walletCurrency"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="currency"
                  :label="$t('filters.label.wallet_currency')"
                  :placeholder="$t('filters.placeholder.wallet_currency')">
              </filter-select>
            </div>
          </div>
          <div class="row">
            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.maxPlayers"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="tournamentPlayersQuantity"
                  :label="$t('tournament.filters.label.max_players')"
                  :placeholder="$t('tournament.filters.placeholder.max_players')">
              </filter-select>
            </div>

            <div class="col-4">
              <filter-input-with-operator
                  :filter-query="filterQuery"
                  :operator-options="filterOptions.comparisonOperator"
                  :operator-multiple="false"
                  :default-operator="{value: 'eq', text: $t('operator.equales')}"
                  :restrict-input="true"
                  restriction-type="decimals"
                  filter-name="prizePoolSum"
                  :label="$t('tournament.filters.label.prize_pool_sum')">
              </filter-input-with-operator>
            </div>

            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.category"
                  :multiple="true"
                  options-label="label"
                  options-track-by="id"
                  :operator="'eq'"
                  filter-name="questionCategoryId"
                  :label="$t('tournament.filters.label.question_category')"
                  :placeholder="$t('tournament.filters.placeholder.question_category')">
              </filter-select>
            </div>
          </div>

          <div class="row">
            <div class="col-4">
              <filter-date-range-picker
                  :filter-query="filterQuery"
                  :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                  :auto="true"
                  :linked="false"
                  :time-picker="true"
                  filter-name="startedAt"
                  open-direction="left"
                  :label="$t('filters.label.tournament_started_at')">
              </filter-date-range-picker>
            </div>

            <div class="col-4">
              <filter-select
                  :filter-query="filterQuery"
                  :options="filterOptions.status"
                  :multiple="false"
                  options-label="text"
                  options-track-by="value"
                  filter-name="status"
                  :label="$t('tournament.filters.label.status')"
                  :placeholder="$t('tournament.filters.placeholder.status')">
              </filter-select>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:footer>
        <b-button @click="resetFilters()" variant="secondary" class="mr-1">{{$t('filters.btn.clear')}}</b-button>
        <b-button @click="applyFilters()" variant="primary">{{$t('filters.btn.apply')}}</b-button>
      </template>
    </page-card>

    <page-card>
      <template v-slot:header>
        {{ $t('page.list')}}
      </template>
      <template v-slot:content>
        <div class="sygni-grid">
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>
          <div class="table-responsive">
            <b-table striped
                     :busy="isBusy"
                     hover
                     head-variant="dark"
                     outlined
                     :sort-by.sync="orderColumn"
                     :sort-desc.sync="orderDirection"
                     :empty-text="$t('table.no_results')"
                     :no-local-sorting="true"
                     :items="items"
                     :fields="fields">

              <template #cell(id)="data">
                {{ data.item.id.split('-')[0] }}
              </template>

              <template #cell(startedAt)="data">
                {{ data.item.startedAt | dateFormat('YYYY-MM-DD HH:mm:ss') }}
              </template>

              <template #cell(buyIn)="data">
                {{ data.value | numberFormat }}
              </template>

              <template #cell(currency)="data">
                {{ $t(data.value)  }}
              </template>

              <template #cell(categoryLabel)="data">
                {{ $t(`category.${data.value}`) }}
              </template>

              <template #cell(status)="data">
                <b-badge pill variant="success" v-if="data.value === 'FINISHED'">{{ $t(data.value) }}</b-badge>
                <b-badge pill variant="danger" v-if="data.value === 'FAILED'">{{ $t(data.value) }}</b-badge>
              </template>

              <template #cell(actions)="data">
                <div class="table-action--cell">
                  <b-button variant="success" size="sm" @click="openModal(data.item)">{{ $t('table.btn.tournament_ladder') }}</b-button>
                </div>
              </template>

              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>{{$t('state.loading')}}</strong>
                </div>
              </template>
            </b-table>
          </div>
          <grid-pagination :total-count="totalCount"
                           :per-page="perPage"
                           :current-page="currentPage"
                           @load-items="paginationReload"></grid-pagination>
        </div>
      </template>
      <template v-slot:footer>

      </template>
    </page-card>

  </div>
</template>

<script>
import GridMixin from "../../components/grid/GridMixin";
import InstantTournamentHistoryLadderModal from './InstantTournamentHistoryLadderModal';
import FilterSelect from "../../components/grid/filters/FilterSelect";
import FilterInput from "../../components/grid/filters/FilterInput";
import FilterDateRangePicker from "../../components/grid/filters/FilterDateRangePicker";
import FilterInputWithOperator from "../../components/grid/filters/FilterInputWithOperator";
import GridPagination from "../../components/grid/grid-pagination/GridPagination";
import PageHeader from "../../components/page/PageHeader";
import PageCard from "../../components/page/PageCard";
import axios from "axios";

export default {
  mixins: [GridMixin],
  components: {
    PageCard,
    PageHeader,
    InstantTournamentHistoryLadderModal,
    FilterSelect,
    FilterInput,
    FilterDateRangePicker,
    FilterInputWithOperator,
    GridPagination
  },
  data() {
    return {
      gridGetUrl: '/api/tournament/history',
      defaultFilters: '',
      categoryGetUrl: '/api/question/question-category',
      orderColumn: 'startedAt',
      orderDirection: false,
      fields: [
        {key: 'id', label: this.$t('table.header.tournament_id'), sortable: false},
        {key: 'buyIn', label: this.$t('table.header.buy_n'), sortable: false},
        {key: 'currency', label: this.$t('table.header.currency'), sortable: true},
        {key: 'tournamentPlayersQuantity', label: this.$t('table.header.tournament_players_quantity'), sortable: false},
        {key: 'prizePoolSum', label: this.$t('table.header.prize_pool_sum'), sortable: true},
        {key: 'categoryLabel', label: this.$t('table.header.category_label'), sortable: true},
        {key: 'startedAt', label: this.$t('table.header.started_at'), sortable: true},
        {key: 'status', label: this.$t('table.header.status'), sortable: true},
        {key: 'actions', label: this.$t('table.header.actions'), sortable: false}
      ],
      perPage: 10,
      currentPage: 1,
      filterOptions: {
        lobbyId: [
          {value: 'INSTANT', text: this.$t('tournament.options.instant')},
          {value: 'SIT_AND_GO', text: this.$t('tournament.options.sit_and_go')}
        ],
        status: [
          {value: 'FINISHED', text: this.$t('tournament.options.finished')},
          {value: 'FAILED', text: this.$t('tournament.options.failed')}
        ],
        walletCurrency: [
          {value: 'FUN_COINS', text: this.$t('tournament.fun_coins')},
          {value: 'QUIZ_COINS', text: this.$t('tournament.quiz_coins')}
        ],
        maxPlayers: [
          {value: '8', text: '8'},
          {value: '16', text: '16'},
          {value: '32', text: '32'},
          {value: '64', text: '64'},
          {value: '128', text: '128'}
        ],
        comparisonOperator: [
          {value: 'eq', text: this.$t('operator.equales')},
          {value: 'gte', text: this.$t('operator.greater_then')},
          {value: 'lte', text: this.$t('operator.lower_than')},
          {value: 'btw', text: this.$t('operator.between')},
        ],
        category: [],
      },
      filterQuery: [],
      detailsModalInstance: null,

    };
  },
  computed: {
    walletCurrency: function () {
      return this.$store.getters["root/walletCurrency"].map(d => {
        return {
          value: d.value,
          text: this.$t(d.value)
        }
      });
    },
  },
  mounted() {
    this.getData(this.categoryGetUrl+'?limit=1000', ['filterOptions', 'category']);
  },
  methods: {
    getData: function (url, localStorage) {
      let _that = this;
      axios.get(url)
          .then((resp) => {
            if (localStorage[1] === 'category') {
              _that[localStorage[0]][localStorage[1]] = resp.data.items.map(c => {
                return {
                  id: c.id,
                  label: this.$t('category.'+c.name)
                }
              });
            } else {
              _that[localStorage[0]][localStorage[1]] = resp.data.items;
            }
          })
          .catch((err) => {
            console.log(err)
          })
    },
    resetFilters() {
      this.filterQuery = [];
      this.$root.$emit('resetFilters');
      // this.initFilters();
      this.applyFilters();
    },
    openModal(data) {
      const tournamentData = {
        currency: data.currency,
        ladder: data.draw,
        awards: data.awards
      }

      this.$refs['instant-tournament-history-ladder-modal'].showLadder(tournamentData);
    }
  }
}
</script>
